/**
 * ScoreManager - Handles scoring and high score persistence
 * Calculates points based on placement, height, precision, and shape type
 */

/**
 * Configuration for scoring calculations
 */
export interface ScoreConfig {
  /** Base points for placing a piece */
  basePoints: number;
  /** Multiplier for height bonus */
  heightBonusMultiplier: number;
  /** Height interval for bonus calculation (pixels) */
  heightBonusInterval: number;
  /** Distance thresholds for precision bonuses (pixels) */
  precisionThresholds: {
    perfect: number;
    good: number;
  };
  /** Bonus points for precision placement */
  precisionBonuses: {
    perfect: number;
    good: number;
  };
  /** Bonus points for risky shape types */
  riskyShapeBonuses: Record<string, number>;
}

/**
 * Default scoring configuration
 */
export const DEFAULT_SCORE_CONFIG: ScoreConfig = {
  basePoints: 100,
  heightBonusMultiplier: 50,
  heightBonusInterval: 50,
  precisionThresholds: {
    perfect: 5,
    good: 15,
  },
  precisionBonuses: {
    perfect: 200,
    good: 100,
  },
  riskyShapeBonuses: {
    circle: 150,
    triangle: 75,
    square: 0,
  },
};

const STORAGE_KEY = 'zenith_highscore';

/**
 * Manages game scoring and high score persistence
 */
export class ScoreManager {
  private config: ScoreConfig;
  private currentScore: number = 0;
  private highScore: number = 0;
  private piecesPlaced: number = 0;

  /**
   * @param config - Optional scoring configuration
   */
  constructor(config: Partial<ScoreConfig> = {}) {
    this.config = { ...DEFAULT_SCORE_CONFIG, ...config };
    this.loadHighScore();
  }

  /**
   * Calculate score for a piece placement
   * @param shapeType - Type of shape placed (square, circle, triangle)
   * @param height - Height from bottom where piece was placed (pixels)
   * @param offsetFromCenter - Distance from platform center (pixels)
   * @returns Calculated score for this placement
   */
  calculatePlacementScore(
    shapeType: string,
    height: number,
    offsetFromCenter: number
  ): number {
    let score = this.config.basePoints;

    // Height bonus - more points for placing higher
    const heightBonus = Math.floor(height / this.config.heightBonusInterval)
      * this.config.heightBonusMultiplier;
    score += heightBonus;

    // Precision bonus - more points for centering
    if (offsetFromCenter < this.config.precisionThresholds.perfect) {
      score += this.config.precisionBonuses.perfect;
    } else if (offsetFromCenter < this.config.precisionThresholds.good) {
      score += this.config.precisionBonuses.good;
    }

    // Risky shape bonus - circles and triangles are harder to place
    const shapeLower = shapeType.toLowerCase();
    score += this.config.riskyShapeBonuses[shapeLower] || 0;

    return score;
  }

  /**
   * Add points to current score
   * @param points - Points to add
   */
  addScore(points: number): void {
    this.currentScore += points;
    this.piecesPlaced++;

    if (this.currentScore > this.highScore) {
      this.highScore = this.currentScore;
      this.saveHighScore();
    }
  }

  /**
   * Get current score
   */
  getCurrentScore(): number {
    return this.currentScore;
  }

  /**
   * Get high score
   */
  getHighScore(): number {
    return this.highScore;
  }

  /**
   * Get number of pieces placed
   */
  getPiecesPlaced(): number {
    return this.piecesPlaced;
  }

  /**
   * Check if current score is a new high score
   */
  isNewHighScore(): boolean {
    return this.currentScore >= this.highScore && this.currentScore > 0;
  }

  /**
   * Reset current score for new game
   */
  reset(): void {
    this.currentScore = 0;
    this.piecesPlaced = 0;
  }

  /**
   * Load high score from localStorage
   */
  private loadHighScore(): void {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      this.highScore = saved ? parseInt(saved, 10) : 0;
      if (isNaN(this.highScore)) {
        this.highScore = 0;
      }
    } catch {
      // localStorage not available (SSR or privacy mode)
      this.highScore = 0;
    }
  }

  /**
   * Save high score to localStorage
   */
  private saveHighScore(): void {
    try {
      localStorage.setItem(STORAGE_KEY, this.highScore.toString());
    } catch {
      // localStorage not available
    }
  }

  /**
   * Clear high score (for testing)
   */
  clearHighScore(): void {
    this.highScore = 0;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // localStorage not available
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): Readonly<ScoreConfig> {
    return { ...this.config };
  }
}
